import { Store, Action, Getter, Mutation } from 'vuex';
import { RootState } from '@/store/types';
import { GRID_WRAP_INITIALIZE, GRID_WRAP_RESET, GRID_WRAP_SET_COLUMN, USER_SET_DATA } from '@/store/mutation-types';

type GridWrapState = {
  userId: string;
  wrappedColumns: Record<string, boolean>;
};

type InitializePayload = { userId: string };
type SetColumnPayload = { colId: string; isColumnWrapped: boolean };

const getLocalStorageKey = (userId: string) => `atlasticloud${userId}-gridwrapstate`;

const getStateFromStorage = (state: GridWrapState, userId: string): GridWrapState => {
  const item = localStorage.getItem(getLocalStorageKey(userId));

  if (item !== null) {
    return JSON.parse(item);
  }

  return Object.assign({}, state, { userId });
};

const storeConfig = (state: GridWrapState) => {
  localStorage.setItem(getLocalStorageKey(state.userId), JSON.stringify(state));
};

const actions: Record<string, Action<GridWrapState, RootState>> = {
  initialize: ({ commit }, payload: InitializePayload) => {
    commit(GRID_WRAP_INITIALIZE, payload);
  },
  setColumn: ({ commit }, payload: SetColumnPayload) => {
    commit(GRID_WRAP_SET_COLUMN, payload);
  },
  reset: ({ commit }) => {
    commit(GRID_WRAP_RESET);
  },
};

const getters: Record<string, Getter<GridWrapState, RootState>> = {
  isColumnWrapped(state) {
    return (colId: string) => state.wrappedColumns[colId] ?? true;
  },
};

const mutations: Record<string, Mutation<GridWrapState>> = {
  [GRID_WRAP_INITIALIZE](state, { userId }: InitializePayload) {
    const storedState = getStateFromStorage(state, userId);
    state.wrappedColumns = storedState.wrappedColumns;
    state.userId = storedState.userId;
    storeConfig(state);
  },
  [GRID_WRAP_SET_COLUMN](state, { colId, isColumnWrapped }: SetColumnPayload) {
    state.wrappedColumns[colId] = isColumnWrapped;
    if (state.userId !== '') {
      storeConfig(state);
    }
  },
  [GRID_WRAP_RESET](state) {
    state.wrappedColumns = {};
    if (state.userId !== '') {
      storeConfig(state);
    }
  },
};

// Plugin to initialize gridWrap state when user ID is set
const gridWrapInitPlugin = (store: Store<RootState>) => {
  store.subscribe((mutation) => {
    if (mutation.type === `user/${USER_SET_DATA}`) {
      store.dispatch('gridWrap/initialize', { userId: mutation.payload.id });
    }
  });
};

export default {
  namespaced: true,
  state: {
    userId: '',
    wrappedColumns: {},
  } satisfies GridWrapState,
  actions,
  getters,
  mutations,
  plugins: [gridWrapInitPlugin],
};
